import { useMemo, useState } from 'react';
import { Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { TIER_LABEL, type CustomerTier } from '@/features/projections/engine';
import type { ProjectionModel } from '@/features/projections/useProjectionModel';
import {
  useDeleteSalesCustomer,
  useDeleteSalesProduct,
  useSetCellStyle,
  useUpsertSalesCustomer,
  useUpsertSalesProduct,
  type SalesCustomerRow,
  type SalesProductRow,
} from '@/features/projections/workbookHooks';
import { ProjectionsGrid, type ColumnMode } from './ProjectionsGrid';

interface Props {
  pm: ProjectionModel;
  zoom: number;
  columnMode: ColumnMode;
  height: number;
}

/** Virro's launch line-up — used to seed a fresh scenario in one click. */
const DEFAULT_PRODUCTS = [
  {
    name: 'Manual',
    big: 625_00,
    small: 675_00,
    retail: 999_00,
    cogs: 285_00,
    wholesale: 0.5,
    retail_share: 1 / 3,
  },
  {
    name: 'Electric',
    big: 725_00,
    small: 750_00,
    retail: 1149_00,
    cogs: 300_00,
    wholesale: 0.5,
    retail_share: 1 / 3,
  },
  {
    name: 'Flex',
    big: 800_00,
    small: 825_00,
    retail: 1249_00,
    cogs: 350_00,
    wholesale: 0,
    retail_share: 1 / 3,
  },
];

const TIERS: CustomerTier[] = ['wholesale_big', 'wholesale_small', 'retail'];

/**
 * Sales Drivers — the top of the operator's spreadsheet. Enter units per
 * customer per month; the engine splits them across models using the tier
 * mix, prices them by tier, and pushes revenue + COGS into the P&L.
 */
export function SalesDriversTab({ pm, zoom, columnMode, height }: Props) {
  const upsertProduct = useUpsertSalesProduct(pm.scenario);
  const deleteProduct = useDeleteSalesProduct(pm.scenario);
  const upsertCustomer = useUpsertSalesCustomer(pm.scenario);
  const deleteCustomer = useDeleteSalesCustomer(pm.scenario);
  const setStyle = useSetCellStyle(pm.scenario);

  const rows = useMemo(() => pm.buildRows('sales'), [pm]);

  const revenueAccounts = useMemo(
    () => pm.accounts.filter((a) => a.account_type === 'revenue' && a.is_active),
    [pm.accounts],
  );
  const cogsAccounts = useMemo(
    () => pm.accounts.filter((a) => a.account_type === 'expense' && a.is_active),
    [pm.accounts],
  );

  const onError = (err: unknown) =>
    toast.error(err instanceof Error ? err.message : 'Could not save');

  const seedDefaults = () => {
    DEFAULT_PRODUCTS.forEach((p, i) =>
      upsertProduct.mutate(
        {
          name: p.name,
          price_big_cents: p.big,
          price_small_cents: p.small,
          price_retail_cents: p.retail,
          unit_cogs_cents: p.cogs,
          wholesale_share: p.wholesale,
          retail_share: p.retail_share,
          position: i,
          is_active: true,
        },
        { onError },
      ),
    );
    toast.success('Manual, Electric and Flex added — link each to its QuickBooks account');
  };

  if (pm.isLoading || !pm.model) return <Skeleton className="h-[480px] w-full" />;

  const products = pm.products;
  const customers = pm.customers;
  const mixWarnings = pm.model.sales.mixWarnings;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Models &amp; pricing</CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                Tier prices and unit cost per model. Mix = how a distributor's (or a retail
                customer's) units split across models unless a customer overrides it.
              </p>
            </div>
            <div className="flex gap-2">
              {products.length === 0 && (
                <Button variant="outline" size="sm" onClick={seedDefaults}>
                  <Sparkles className="h-3.5 w-3.5" /> Use Virro defaults
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  upsertProduct.mutate(
                    { name: 'New model', position: products.length, is_active: true },
                    { onError },
                  )
                }
              >
                <Plus className="h-3.5 w-3.5" /> Model
              </Button>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto px-0 pb-4">
            <table className="w-full min-w-[880px] text-xs">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Model</th>
                  <th className="px-2 py-2 text-right">Big dist.</th>
                  <th className="px-2 py-2 text-right">Small dist.</th>
                  <th className="px-2 py-2 text-right">Retail</th>
                  <th className="px-2 py-2 text-right">Unit COGS</th>
                  <th className="px-2 py-2 text-right">Wholesale mix</th>
                  <th className="px-2 py-2 text-right">Retail mix</th>
                  <th className="px-2 py-2 text-left">Revenue account</th>
                  <th className="px-2 py-2 text-left">COGS account</th>
                  <th className="px-2 py-2 text-center">On</th>
                  <th className="w-8 px-1 py-2" />
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    revenueAccounts={revenueAccounts}
                    cogsAccounts={cogsAccounts}
                    onSave={(patch) =>
                      upsertProduct.mutate(
                        { ...patch, id: p.id, name: patch.name ?? p.name },
                        { onError },
                      )
                    }
                    onDelete={() => deleteProduct.mutate(p.id, { onError })}
                  />
                ))}
                {products.length > 0 && (
                  <tr className="border-t border-border text-[11px] text-muted-foreground">
                    <td className="px-3 py-1.5" colSpan={5}>
                      Mix columns should each total 100%
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {pct(
                        products
                          .filter((p) => p.is_active)
                          .reduce((s, p) => s + p.wholesale_share, 0),
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {pct(
                        products.filter((p) => p.is_active).reduce((s, p) => s + p.retail_share, 0),
                      )}
                    </td>
                    <td colSpan={4} />
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Customers</CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                Tier sets the price list and the default model split.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                upsertCustomer.mutate(
                  {
                    name: 'New customer',
                    tier: 'wholesale_small',
                    position: customers.length,
                    is_active: true,
                  },
                  { onError },
                )
              }
            >
              <Plus className="h-3.5 w-3.5" /> Customer
            </Button>
          </CardHeader>
          <CardContent className="px-0 pb-4">
            <table className="w-full text-xs">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-2 py-2 text-left">Tier</th>
                  <th className="px-2 py-2 text-left">Model mix</th>
                  <th className="px-2 py-2 text-center">On</th>
                  <th className="w-8 px-1 py-2" />
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <CustomerRow
                    key={c.id}
                    customer={c}
                    products={products}
                    onSave={(patch) =>
                      upsertCustomer.mutate(
                        { ...patch, id: c.id, name: patch.name ?? c.name },
                        { onError },
                      )
                    }
                    onDelete={() => deleteCustomer.mutate(c.id, { onError })}
                  />
                ))}
                {customers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                      Add your distributors and retail channels, then type monthly units in the grid
                      below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {mixWarnings.length > 0 && (
              <ul className="mx-3 mt-2 space-y-1 rounded-md bg-amber-50 p-2 text-[11px] text-amber-900">
                {mixWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Units by customer, by month</h3>
          <p className="text-xs text-muted-foreground">
            Type units on a customer row. Model split, revenue and COGS update below it and flow
            into the P&amp;L and cash sheets.
          </p>
        </div>
        <ProjectionsGrid
          sheet="sales"
          rows={rows}
          model={pm.model}
          visibleMonths={pm.visibleMonths}
          settings={pm.settings}
          styles={pm.styles}
          zoom={zoom}
          columnMode={columnMode}
          viewMode="amount"
          height={height}
          onEdit={pm.applyEdits}
          onSetStyle={(input) => setStyle.mutate(input, { onError })}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;

function MoneyInput({
  cents,
  onCommit,
  className,
}: {
  cents: number;
  onCommit: (cents: number) => void;
  className?: string;
}) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
  return (
    <Input
      value={shown}
      onFocus={() => setText((cents / 100).toString())}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text === null) return;
        const v = Number(text.replace(/[$,\s]/g, ''));
        if (Number.isFinite(v) && Math.round(v * 100) !== cents) onCommit(Math.round(v * 100));
        setText(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className={`h-7 w-24 px-2 text-right text-xs ${className ?? ''}`}
      inputMode="decimal"
    />
  );
}

function PctInput({ value, onCommit }: { value: number; onCommit: (v: number) => void }) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (Math.round(value * 1000) / 10).toString();
  return (
    <div className="flex items-center justify-end gap-1">
      <Input
        value={shown}
        onFocus={() => setText((Math.round(value * 1000) / 10).toString())}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text === null) return;
          const v = Number(text.replace(/[%\s]/g, ''));
          if (Number.isFinite(v)) onCommit(Math.max(0, Math.min(1, v / 100)));
          setText(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        className="h-7 w-16 px-2 text-right text-xs"
        inputMode="decimal"
      />
      <span className="text-muted-foreground">%</span>
    </div>
  );
}

function TextInput({
  value,
  onCommit,
  className,
}: {
  value: string;
  onCommit: (v: string) => void;
  className?: string;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <Input
      value={text ?? value}
      onFocus={() => setText(value)}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text !== null && text.trim() && text.trim() !== value) onCommit(text.trim());
        setText(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className={`h-7 px-2 text-xs ${className ?? ''}`}
    />
  );
}

function AccountSelect({
  value,
  accounts,
  onChange,
  placeholder,
}: {
  value: string | null;
  accounts: Array<{ id: string; name: string; code: string | null }>;
  onChange: (id: string | null) => void;
  placeholder: string;
}) {
  return (
    <Select value={value ?? 'none'} onValueChange={(v) => onChange(v === 'none' ? null : v)}>
      <SelectTrigger className="h-7 w-44 text-xs">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">
          <span className="text-muted-foreground">{placeholder}</span>
        </SelectItem>
        {accounts.map((a) => (
          <SelectItem key={a.id} value={a.id}>
            {a.code ? `${a.code} · ` : ''}
            {a.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ProductRow({
  product: p,
  revenueAccounts,
  cogsAccounts,
  onSave,
  onDelete,
}: {
  product: ProjectionModel['products'][number];
  revenueAccounts: Array<{ id: string; name: string; code: string | null }>;
  cogsAccounts: Array<{ id: string; name: string; code: string | null }>;
  onSave: (patch: Partial<SalesProductRow>) => void;
  onDelete: () => void;
}) {
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="px-3 py-1.5">
        <TextInput value={p.name} onCommit={(name) => onSave({ name })} className="w-32" />
      </td>
      <td className="px-2 py-1.5 text-right">
        <MoneyInput cents={p.price_big_cents} onCommit={(c) => onSave({ price_big_cents: c })} />
      </td>
      <td className="px-2 py-1.5 text-right">
        <MoneyInput
          cents={p.price_small_cents}
          onCommit={(c) => onSave({ price_small_cents: c })}
        />
      </td>
      <td className="px-2 py-1.5 text-right">
        <MoneyInput
          cents={p.price_retail_cents}
          onCommit={(c) => onSave({ price_retail_cents: c })}
        />
      </td>
      <td className="px-2 py-1.5 text-right">
        <MoneyInput cents={p.unit_cogs_cents} onCommit={(c) => onSave({ unit_cogs_cents: c })} />
      </td>
      <td className="px-2 py-1.5">
        <PctInput value={p.wholesale_share} onCommit={(v) => onSave({ wholesale_share: v })} />
      </td>
      <td className="px-2 py-1.5">
        <PctInput value={p.retail_share} onCommit={(v) => onSave({ retail_share: v })} />
      </td>
      <td className="px-2 py-1.5">
        <AccountSelect
          value={p.revenue_account_id}
          accounts={revenueAccounts}
          onChange={(id) => onSave({ revenue_account_id: id })}
          placeholder="Revenue account"
        />
      </td>
      <td className="px-2 py-1.5">
        <AccountSelect
          value={p.cogs_account_id}
          accounts={cogsAccounts}
          onChange={(id) => onSave({ cogs_account_id: id })}
          placeholder="COGS account"
        />
      </td>
      <td className="px-2 py-1.5 text-center">
        <Switch checked={p.is_active} onCheckedChange={(v) => onSave({ is_active: v })} />
      </td>
      <td className="px-1 py-1.5">
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`Delete ${p.name}? Unit history for customers is kept.`)) onDelete();
          }}
          className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-destructive"
          aria-label={`Delete ${p.name}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

function CustomerRow({
  customer: c,
  products,
  onSave,
  onDelete,
}: {
  customer: ProjectionModel['customers'][number];
  products: ProjectionModel['products'];
  onSave: (patch: Partial<SalesCustomerRow>) => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, number> | null>(null);
  const mix = draft ?? c.mix_override ?? null;
  const activeProducts = products.filter((p) => p.is_active);
  const sum = mix ? activeProducts.reduce((s, p) => s + (mix[p.id] ?? 0), 0) : 1;
  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="px-3 py-1.5">
        <TextInput value={c.name} onCommit={(name) => onSave({ name })} className="w-40" />
      </td>
      <td className="px-2 py-1.5">
        <Select value={c.tier} onValueChange={(v) => onSave({ tier: v as CustomerTier })}>
          <SelectTrigger className="h-7 w-36 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIERS.map((t) => (
              <SelectItem key={t} value={t}>
                {TIER_LABEL[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-1.5">
        <Popover onOpenChange={(open) => !open && setDraft(null)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="rounded border border-border px-2 py-1 text-[11px] hover:bg-secondary"
            >
              {c.mix_override ? 'Custom mix' : 'Tier default'}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 space-y-2 text-xs">
            <p className="font-medium">Model split for {c.name}</p>
            <p className="text-muted-foreground">
              Leave on tier default (50/50 for distributors, ⅓ each for retail) unless this customer
              has told you otherwise.
            </p>
            {activeProducts.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2">
                <span>{p.name}</span>
                <PctInput
                  value={
                    mix
                      ? (mix[p.id] ?? 0)
                      : c.tier === 'retail'
                        ? p.retail_share
                        : p.wholesale_share
                  }
                  onCommit={(v) => {
                    const base: Record<string, number> = {};
                    for (const q of activeProducts)
                      base[q.id] =
                        mix?.[q.id] ?? (c.tier === 'retail' ? q.retail_share : q.wholesale_share);
                    base[p.id] = v;
                    setDraft(base);
                  }}
                />
              </div>
            ))}
            {mix && (
              <p
                className={Math.abs(sum - 1) > 0.001 ? 'text-destructive' : 'text-muted-foreground'}
              >
                Total {pct(sum)}
              </p>
            )}
            <div className="flex justify-between pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(null);
                  onSave({ mix_override: null });
                }}
              >
                Use tier default
              </Button>
              <Button
                size="sm"
                disabled={!draft}
                onClick={() => {
                  if (draft) onSave({ mix_override: draft });
                  setDraft(null);
                }}
              >
                Save mix
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </td>
      <td className="px-2 py-1.5 text-center">
        <Switch checked={c.is_active} onCheckedChange={(v) => onSave({ is_active: v })} />
      </td>
      <td className="px-1 py-1.5">
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`Delete ${c.name} and their unit projections?`)) onDelete();
          }}
          className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-destructive"
          aria-label={`Delete ${c.name}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}
